import { useState, useCallback } from 'react';
import { FlaskConical, RotateCcw, X, Clock } from 'lucide-react';
import { useAuth, api } from '../contexts/AuthContext';
import { toast } from 'sonner';

export default function DemoBanner() {
  const { user } = useAuth();
  const [resetting, setResetting] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const isDemo = user?.subscription?.is_demo;
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';

  const handleReset = useCallback(async () => {
    if (!window.confirm(
      'Reset all demo data to defaults?\n\nThis will clear all sales, POs, and expenses made during this demo session and restore the original sample data. This cannot be undone.'
    )) return;

    setResetting(true);
    try {
      await api.post('/demo/reset');
      toast.success('Demo data reset! Refreshing in 2 seconds…');
      setTimeout(() => window.location.reload(), 2000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset failed. Please try again.');
    } finally {
      setResetting(false);
    }
  }, []);

  if (!isDemo || dismissed) return null;

  return (
    <div
      data-testid="demo-banner"
      className="shrink-0 flex items-center justify-between gap-3 px-4 py-2.5 border-b"
      style={{
        backgroundColor: 'var(--info-tint)',
        borderColor: 'var(--info-bg)',
      }}
    >
      {/* Left: Icon + text */}
      <div className="flex items-center gap-3 min-w-0">
        <span
          className="inline-flex items-center justify-center w-7 h-7 rounded-md flex-shrink-0"
          style={{ backgroundColor: 'var(--info-bg)' }}
        >
          <FlaskConical size={13} style={{ color: 'var(--info-icon)' }} />
        </span>
        <span className="text-[13px] font-medium truncate" style={{ color: 'var(--info-text)' }}>
          <span className="font-semibold">Demo Account</span>
          {' '}— fully interactive. Explore freely.
        </span>
        <span className="hidden sm:flex items-center gap-1 text-[11px] flex-shrink-0" style={{ color: 'var(--text-tertiary)' }}>
          <Clock size={10} />
          Resets daily at 3:00 AM
        </span>
      </div>

      {/* Right: Reset button + dismiss */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {isAdmin && (
          <button
            data-testid="demo-reset-btn"
            onClick={handleReset}
            disabled={resetting}
            className="flex items-center gap-1.5 text-[11px] font-semibold px-2.5 h-7 rounded-md border transition-colors duration-75 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: 'var(--info-bg)',
              borderColor: 'var(--info-icon)',
              color: 'var(--info-text)',
            }}
          >
            <RotateCcw size={11} className={resetting ? 'animate-spin' : ''} />
            {resetting ? 'Resetting…' : 'Reset Demo'}
          </button>
        )}
        <button
          data-testid="demo-banner-dismiss"
          onClick={() => setDismissed(true)}
          className="p-0.5 rounded transition-colors duration-75"
          style={{ color: 'var(--text-tertiary)' }}
          title="Dismiss"
        >
          <X size={13} />
        </button>
      </div>
    </div>
  );
}
